import { useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Trash2, Loader2, Check, Upload, AlertTriangle } from "lucide-react";

/**
 * Modal para actualización masiva de PVL.
 *
 * Cada línea representa un cambio, que puede ser por:
 *   - G.H./Molécula → actualiza TODOS los CN de esa molécula
 *   - CN individual → actualiza sólo ese CN
 * Se distingue con el campo `keyType`.
 *
 * Props:
 *   items: array del catálogo actual (para autocompletar G.H. y buscar CN)
 *   onClose: () => void
 *   onDone: () => void
 */
export default function BulkPvlUpdateModal({ items, onClose, onDone }) {
  // { keyType: 'gh'|'cn', key: string, pvl: string }
  const [rows, setRows] = useState([{ keyType: "gh", key: "", pvl: "" }]);
  const [applying, setApplying] = useState(false);
  const [parsing, setParsing] = useState(false);
  const fileInputRef = useRef(null);

  const ghOptions = useMemo(() => {
    const s = new Set();
    for (const it of items) {
      const g = (it.gh || "").trim();
      if (g) s.add(g);
    }
    return Array.from(s).sort();
  }, [items]);

  // Índices para preview en vivo
  const previewByGh = useMemo(() => {
    const map = {};
    for (const it of items) {
      const g = (it.gh || "").trim();
      if (!g) continue;
      const slot = map[g] || { count: 0, pvls: [] };
      slot.count += 1;
      if (it.pvl != null) slot.pvls.push(Number(it.pvl));
      map[g] = slot;
    }
    return map;
  }, [items]);

  const previewByCn = useMemo(() => {
    const map = {};
    for (const it of items) {
      const cn = String(it.cn || "").trim();
      if (!cn) continue;
      map[cn] = it;
      // También indexamos con ceros a la izquierda quitados
      const stripped = cn.replace(/^0+/, "");
      if (stripped && stripped !== cn) map[stripped] = it;
    }
    return map;
  }, [items]);

  const addRow = (keyType = "gh") =>
    setRows((prev) => [...prev, { keyType, key: "", pvl: "" }]);
  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));
  const setRow = (i, patch) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const validRows = rows.filter((r) => r.key.trim() && r.pvl !== "" && !isNaN(Number(r.pvl)));
  const totalCns = validRows.reduce((sum, r) => {
    if (r.keyType === "cn") return sum + (previewByCn[r.key.trim()] ? 1 : 0);
    return sum + (previewByGh[r.key.trim()]?.count || 0);
  }, 0);

  const apply = async () => {
    if (!validRows.length) {
      toast.error("Añade al menos una línea válida (G.H. o CN + PVL)");
      return;
    }
    if (totalCns > 20) {
      if (!window.confirm(`Se actualizarán ${totalCns} CN(s) del catálogo global. ¿Continuar?`)) return;
    }
    setApplying(true);
    try {
      const { data } = await api.post("/catalog/update-pvl-by-gh", {
        updates: validRows.map((r) =>
          r.keyType === "cn"
            ? { cn: r.key.trim(), pvl: Number(r.pvl) }
            : { gh: r.key.trim(), pvl: Number(r.pvl) },
        ),
      });
      const notFound = (data.summary || []).filter((s) => s.skipped === "cn no encontrado").length;
      if (notFound > 0) {
        toast.warning(`${data.total_cns_updated} CN(s) actualizados · ${notFound} no encontrados en el catálogo.`);
      } else {
        toast.success(`${data.total_cns_updated} CN(s) actualizados en ${data.summary.length} línea(s).`);
      }
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar");
    } finally {
      setApplying(false);
    }
  };

  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/catalog/update-pvl-by-gh/parse-excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const parsed = data.updates || [];
      if (!parsed.length) {
        toast.error("No se detectó ninguna fila válida (¿CN o G.H. + PVL?)");
        return;
      }
      setRows(parsed.map((u) => ({
        keyType: u.cn ? "cn" : "gh",
        key: u.cn || u.gh || "",
        pvl: String(u.pvl),
      })));
      const err = data.errors || [];
      const unchanged = data.unchanged || 0;
      const detectedType = data.key_type === "cn" ? "por CN individual" : "por G.H./Molécula";
      const parts = [`${parsed.length} filas con cambios ${detectedType}`];
      if (unchanged > 0) parts.push(`${unchanged} sin cambios (mismo PVL) omitidas`);
      if (err.length) parts.push(`${err.length} con error`);
      if (unchanged > 0 && parsed.length === 0) {
        toast.info(`Nada que actualizar: los ${unchanged} CN ya tienen ese PVL.`);
      } else if (err.length) {
        toast.warning(parts.join(" · "));
      } else {
        toast.success(parts.join(" · "));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo importar el Excel");
    } finally {
      setParsing(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 sm:p-8"
      onClick={onClose}
      data-testid="bulk-pvl-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-4 flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-slate-200 flex-shrink-0">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
              Catálogo · Actualización masiva
            </div>
            <h2 className="text-xl font-bold text-slate-900 mt-1">Actualizar PVL (por G.H. o CN)</h2>
            <p className="text-xs text-slate-500 mt-1">
              Elige G.H. para actualizar TODOS los CN de la molécula, o CN para actualizar sólo ese CN.
              También puedes importar Excel/CSV.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label
              className="cursor-pointer inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-800 px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-slate-50"
              title="Importar Excel/CSV con columnas [CN o G.H., PVL]"
              data-testid="bulk-pvl-import-btn"
            >
              {parsing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Importar Excel
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.xlsm,.csv"
                className="hidden"
                onChange={handleFileImport}
                data-testid="bulk-pvl-file-input"
              />
            </label>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-700"
              data-testid="close-bulk-pvl-modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-3">
          <datalist id="bulk-pvl-gh-list">
            {ghOptions.map((g) => (
              <option key={g} value={g} />
            ))}
          </datalist>

          {/* Barra de acción superior sticky — para no tener que hacer scroll hasta abajo con Excels grandes */}
          {totalCns > 0 && (
            <div
              className="sticky top-0 z-10 -mx-6 px-6 py-3 bg-white border-b border-slate-200 flex items-center justify-between gap-3 shadow-sm"
              data-testid="bulk-pvl-top-action-bar"
            >
              <div className="text-sm text-slate-700">
                <strong className="text-slate-950">{totalCns}</strong> CN(s) del catálogo se actualizarán ·{" "}
                <strong>{validRows.length}</strong> línea(s) válida(s)
              </div>
              <button
                onClick={apply}
                disabled={applying || !validRows.length}
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2 rounded-md disabled:opacity-60"
                data-testid="bulk-pvl-apply-top"
              >
                {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Aplicar cambios
              </button>
            </div>
          )}

          {rows.map((r, i) => {
            const keyTrim = r.key.trim();
            const isCn = r.keyType === "cn";
            const cnMatch = isCn ? previewByCn[keyTrim] : null;
            const ghPreview = !isCn ? previewByGh[keyTrim] : null;
            const pvls = ghPreview?.pvls || [];
            const pvlMin = pvls.length ? Math.min(...pvls) : null;
            const pvlMax = pvls.length ? Math.max(...pvls) : null;

            return (
              <div
                key={i}
                className="grid grid-cols-12 gap-2 items-start p-3 border border-slate-200 rounded-lg bg-slate-50/40"
              >
                <div className="col-span-2">
                  <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                    Tipo
                  </label>
                  <select
                    value={r.keyType}
                    onChange={(e) => setRow(i, { keyType: e.target.value, key: "" })}
                    className="w-full px-2 py-2 border border-slate-300 rounded-md text-sm bg-white"
                    data-testid={`bulk-pvl-type-${i}`}
                  >
                    <option value="gh">G.H.</option>
                    <option value="cn">CN</option>
                  </select>
                </div>
                <div className="col-span-5">
                  <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                    {isCn ? "Código Nacional (CN)" : "G.H. / Molécula"}
                  </label>
                  <input
                    type="text"
                    list={isCn ? undefined : "bulk-pvl-gh-list"}
                    placeholder={isCn ? "Ej: 707965" : "Empieza a escribir para buscar…"}
                    value={r.key}
                    onChange={(e) => setRow(i, { key: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                    data-testid={`bulk-pvl-key-${i}`}
                  />
                  {keyTrim && (
                    <p className="mt-1 text-[10px]">
                      {isCn ? (
                        cnMatch ? (
                          <span className="text-emerald-700">
                            <Check className="h-3 w-3 inline" /> {cnMatch.name} ·
                            {" "}PVL actual{" "}
                            {cnMatch.pvl != null ? `${Number(cnMatch.pvl).toFixed(2).replace(".", ",")} €` : "—"}
                          </span>
                        ) : (
                          <span className="text-rose-700">
                            <AlertTriangle className="h-3 w-3 inline" /> CN no encontrado en el catálogo.
                          </span>
                        )
                      ) : ghPreview ? (
                        <span className="text-emerald-700">
                          <Check className="h-3 w-3 inline" /> {ghPreview.count} CN(s) · PVL actual{" "}
                          {pvlMin != null ? (
                            <>
                              {pvlMin.toFixed(2).replace(".", ",")} €
                              {pvlMin !== pvlMax && ` – ${pvlMax.toFixed(2).replace(".", ",")} €`}
                            </>
                          ) : (
                            "—"
                          )}
                        </span>
                      ) : (
                        <span className="text-rose-700">G.H. no encontrado en el catálogo.</span>
                      )}
                    </p>
                  )}
                </div>
                <div className="col-span-4">
                  <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                    Nuevo PVL (€)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0,00"
                    value={r.pvl}
                    onChange={(e) => setRow(i, { pvl: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono-num"
                    data-testid={`bulk-pvl-pvl-${i}`}
                  />
                </div>
                <div className="col-span-1 flex items-end justify-end">
                  {rows.length > 1 && (
                    <button
                      onClick={() => removeRow(i)}
                      className="p-2 text-slate-400 hover:text-rose-600"
                      title="Quitar línea"
                      data-testid={`bulk-pvl-remove-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => addRow("gh")}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900"
              data-testid="bulk-pvl-add-row-gh"
            >
              <Plus className="h-3.5 w-3.5" /> Añadir línea G.H.
            </button>
            <button
              onClick={() => addRow("cn")}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900"
              data-testid="bulk-pvl-add-row-cn"
            >
              <Plus className="h-3.5 w-3.5" /> Añadir línea CN
            </button>
          </div>

          <div className="text-[10px] text-slate-500 italic border-t border-dashed border-slate-200 pt-3">
            <span className="font-semibold not-italic text-slate-600">Formato Excel/CSV:</span>{" "}
            2 columnas. La cabecera puede ser <code>CN</code> (o <code>Cnm</code>, <code>Código</code>) para actualizar
            por código individual, o <code>G.H.</code> (o <code>Molécula</code>) para actualizar toda la molécula.
            La segunda columna: <code>PVL</code> / <code>Precio</code>. Si no hay cabecera, se detecta el tipo
            automáticamente (números de 5-8 dígitos → CN).
          </div>

          {totalCns > 0 && (
            <div className="rounded-md bg-slate-900 text-white px-4 py-3 text-sm flex items-center justify-between">
              <span>
                Total: <strong>{totalCns}</strong> CN(s) del catálogo global se actualizarán.
              </span>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900"
          >
            Cancelar
          </button>
          <button
            onClick={apply}
            disabled={applying || !validRows.length}
            className="inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60"
            data-testid="bulk-pvl-apply"
          >
            {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Aplicar cambios
          </button>
        </div>
      </div>
    </div>
  );
}
